import React, { useState } from 'react';
import {
  Card,
  Button,
  Upload,
  Select,
  Typography,
  Table,
  Tag,
  Space,
  Alert,
  Spin,
  Statistic,
  Row,
  Col,
  Divider,
  Empty,
  message,
  Descriptions,
  List,
  Modal,
  Form,
  InputNumber,
  Input,
} from 'antd';
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  MailOutlined,
  TeamOutlined,
  SendOutlined,
  FileAddOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import ConfigSuggestionPanel from './ConfigSuggestionPanel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';

const { Title, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

export default function TestPlaygroundPanel({ configs = [] }) {
  useTranslation(['tables', 'forms', 'messages']);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [emlFile, setEmlFile] = useState(null);
  const [absoftFile, setAbsoftFile] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showSuggestionPanel, setShowSuggestionPanel] = useState(false);
  const [submissionForm] = Form.useForm();

  // Translation helper for smart parser
  // const sp = (key) => t(`smartParser.${key}`, { ns: 'tables', defaultValue: key });

  // Active and testing configs
  const availableConfigs = configs.filter((c) => c.status === 'active' || c.status === 'testing' || c.status === 'draft');

  // Fetch automation companies for submission
  const { data: companiesData } = useQuery({
    queryKey: ['automation-companies-dropdown'],
    queryFn: () => API.payrollAutomation.getAutomationCompanies({ limit: 100 }),
  });

  const automationCompanies = companiesData?.data || [];

  // Test with EML mutation
  const testMutation = useMutation({
    mutationFn: ({ configId, formData }) => API.smartParser.testConfigWithEML(configId, formData),
    onSuccess: (response) => {
      setTestResult(response.data);
      message.success('Test completed successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Test failed');
    },
  });

  // Process EML mutation (production)
  const processMutation = useMutation({
    mutationFn: (formData) => API.smartParser.processEML(formData),
    onSuccess: (response) => {
      setTestResult(response.data);
      message.success('Processing completed successfully');
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Processing failed');
    },
  });

  // Create submission mutation
  const createSubmissionMutation = useMutation({
    mutationFn: (data) => API.smartParser.createSubmission(data),
    onSuccess: (response) => {
      message.success(`Submission created successfully! ID: ${response.data.submissionId}`);
      setShowSubmissionModal(false);
      submissionForm.resetFields();
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to create submission');
    },
  });

  const handleEmlUpload = (info) => {
    const { file } = info;
    if (file.status === 'removed') {
      setEmlFile(null);
      return;
    }
    setEmlFile(file.originFileObj || file);
  };

  const handleAbsoftUpload = (info) => {
    const { file } = info;
    if (file.status === 'removed') {
      setAbsoftFile(null);
      return;
    }
    setAbsoftFile(file.originFileObj || file);
  };

  const handleTest = async () => {
    if (!selectedConfigId) {
      message.warning('Please select a configuration first');
      return;
    }
    if (!emlFile) {
      message.warning('Please upload an email file (.eml or .msg)');
      return;
    }

    const formData = new FormData();
    formData.append('eml', emlFile);
    if (absoftFile) {
      formData.append('absoft', absoftFile);
    }

    testMutation.mutate({ configId: selectedConfigId, formData });
  };

  const handleProcess = async () => {
    if (!selectedConfigId) {
      message.warning('Please select a configuration first');
      return;
    }
    if (!emlFile) {
      message.warning('Please upload an email file (.eml or .msg)');
      return;
    }
    if (!absoftFile) {
      message.warning('Please upload an AB Soft file for production processing');
      return;
    }

    const selectedConfig = configs.find((c) => c._id === selectedConfigId);
    if (selectedConfig?.status !== 'active') {
      message.warning('Only active configurations can be used for production processing');
      return;
    }

    const formData = new FormData();
    formData.append('eml', emlFile);
    formData.append('absoft', absoftFile);
    formData.append('configId', selectedConfigId);

    processMutation.mutate(formData);
  };

  const handleReset = () => {
    setEmlFile(null);
    setAbsoftFile(null);
    setTestResult(null);
  };

  // Handler for config selection from suggestion panel
  const handleSuggestedConfigSelect = (configId, suggestion) => {
    setSelectedConfigId(configId);
    setShowSuggestionPanel(false);
    message.success(`Selected: ${suggestion.configName} (${suggestion.score}% match)`);
  };

  const handleOpenSubmissionModal = () => {
    // Pre-fill with detected values from EML metadata
    const currentDate = new Date();
    const emlMetadata = testResult?.emlMetadata;

    // Try to get month/year from EML metadata or use current
    let month = emlMetadata?.month || currentDate.getMonth() + 1;
    let year = emlMetadata?.year || currentDate.getFullYear();

    // If month is current month, likely it's for previous month
    if (month === currentDate.getMonth() + 1) {
      month = month === 1 ? 12 : month - 1;
      year = month === 12 ? year - 1 : year;
    }

    // Try to find matching company from config
    const selectedConfig = configs.find((c) => c._id === selectedConfigId);
    const companyId = selectedConfig?.companyId?._id || selectedConfig?.companyId;

    submissionForm.setFieldsValue({
      month,
      year,
      companyId,
    });

    setShowSubmissionModal(true);
  };

  const handleCreateSubmission = async (values) => {
    const employees = testResult?.employees || [];

    if (employees.length === 0) {
      message.error('No employees to submit');
      return;
    }

    createSubmissionMutation.mutate({
      companyId: values.companyId,
      month: values.month,
      year: values.year,
      projectCode: values.projectCode,
      employees,
      configId: selectedConfigId,
      emlMetadata: testResult?.emlMetadata,
    });
  };

  const selectedConfig = configs.find((c) => c._id === selectedConfigId);
  const isLoading = testMutation.isPending || processMutation.isPending;

  const employeeColumns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Employee',
      key: 'name',
      render: (_, record) => (
        <div>
          <Text strong>{record.officialName || record.employeeName || '-'}</Text>
          {record.jmbg && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                JMBG: {record.jmbg}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Regular',
      dataIndex: 'regularHours',
      key: 'regular',
      align: 'center',
      render: (val) => val || '-',
    },
    {
      title: 'Overtime',
      dataIndex: 'overtime',
      key: 'overtime',
      align: 'center',
      render: (val) => (val ? <Text type="warning">{val}</Text> : '-'),
    },
    {
      title: 'Public Holiday',
      dataIndex: 'publicHolidayHours',
      key: 'holiday',
      align: 'center',
      render: (val) => (val ? <Tag color="red">{val}</Tag> : '-'),
    },
    {
      title: 'Annual Leave',
      dataIndex: 'annualLeave',
      key: 'annualLeave',
      align: 'center',
      render: (val) => (val ? <Tag color="green">{val}</Tag> : '-'),
    },
    {
      title: 'Sick Leave',
      dataIndex: 'sickLeave',
      key: 'sickLeave',
      align: 'center',
      render: (val) => (val ? <Tag color="blue">{val}</Tag> : '-'),
    },
    {
      title: 'Total',
      dataIndex: 'totalHours',
      key: 'total',
      align: 'center',
      render: (val) => <Text strong>{val || '-'}</Text>,
    },
    {
      title: 'Match',
      key: 'match',
      align: 'center',
      width: 100,
      render: (_, record) => {
        if (record.matchType === 'jmbg') {
          return <Tag color="green">JMBG</Tag>;
        }
        if (record.matchType === 'name') {
          return <Tag color="blue">{record.matchConfidence}%</Tag>;
        }
        if (record.matchType === 'manual') {
          return <Tag color="purple">Manual</Tag>;
        }
        return <Tag color="red">No Match</Tag>;
      },
    },
  ];

  const renderTestForm = () => (
    <Row gutter={24}>
      <Col span={8}>
        <Card
          title="Select Configuration"
          size="small"
          extra={
            <Button
              type="link"
              size="small"
              icon={<BulbOutlined />}
              onClick={() => setShowSuggestionPanel(true)}
            >
              Auto-suggest
            </Button>
          }
        >
          <Select
            placeholder="Choose parser configuration"
            style={{ width: '100%', marginBottom: 16 }}
            value={selectedConfigId}
            onChange={setSelectedConfigId}
            optionLabelProp="label"
          >
            {availableConfigs.map((config) => (
              <Option key={config._id} value={config._id} label={config.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{config.name}</span>
                  <Tag color={config.status === 'active' ? 'green' : config.status === 'testing' ? 'orange' : 'default'}>
                    {config.status}
                  </Tag>
                </div>
              </Option>
            ))}
          </Select>

          {selectedConfig && (
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Status">
                <Tag color={selectedConfig.status === 'active' ? 'green' : 'orange'}>
                  {selectedConfig.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Version">
                v{selectedConfig.version}
              </Descriptions.Item>
              <Descriptions.Item label="Company">
                {selectedConfig.companyId?.name || 'Not assigned'}
              </Descriptions.Item>
              {selectedConfig.testHistory?.length > 0 && (
                <Descriptions.Item label="Last Test">
                  {selectedConfig.testHistory[selectedConfig.testHistory.length - 1]?.accuracy?.toFixed(0)}% accuracy
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Card>
      </Col>

      <Col span={8}>
        <Card title="Upload Email File" size="small">
          <Dragger
            accept=".eml,.msg"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleEmlUpload}
            fileList={emlFile ? [emlFile] : []}
            style={{ marginBottom: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <MailOutlined style={{ fontSize: 32, color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13 }}>
              Drop .eml or .msg file here
            </p>
          </Dragger>
        </Card>
      </Col>

      <Col span={8}>
        <Card title="Upload AB Soft File" size="small" extra={<Tag>Optional for test</Tag>}>
          <Dragger
            accept=".xls,.xlsx,.xlsm"
            maxCount={1}
            beforeUpload={() => false}
            onChange={handleAbsoftUpload}
            fileList={absoftFile ? [absoftFile] : []}
            style={{ marginBottom: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <TeamOutlined style={{ fontSize: 32, color: '#52c41a' }} />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13 }}>
              Drop AB Soft employee list
            </p>
          </Dragger>
        </Card>
      </Col>
    </Row>
  );

  const renderActions = () => (
    <div style={{ marginTop: 16, textAlign: 'center' }}>
      <Space size="large">
        <Button
          type="primary"
          size="large"
          icon={<PlayCircleOutlined />}
          loading={testMutation.isPending}
          onClick={handleTest}
          disabled={!selectedConfigId || !emlFile || isLoading}
        >
          Test Configuration
        </Button>

        {selectedConfig?.status === 'active' && absoftFile && (
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            loading={processMutation.isPending}
            onClick={handleProcess}
            disabled={!selectedConfigId || !emlFile || !absoftFile || isLoading}
            style={{ background: '#52c41a', borderColor: '#52c41a' }}
          >
            Process for Production
          </Button>
        )}
      </Space>
    </div>
  );

  const renderTestResults = () => (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Parsing Accuracy"
              value={testResult.accuracy?.parsing || 0}
              suffix="%"
              valueStyle={{
                color: (testResult.accuracy?.parsing || 0) >= 90 ? '#52c41a' : '#faad14',
              }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Matching Accuracy"
              value={testResult.accuracy?.matching || 0}
              suffix="%"
              valueStyle={{
                color: (testResult.accuracy?.matching || 0) >= 90 ? '#52c41a' : '#faad14',
              }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Employees Parsed"
              value={testResult.parsedEmployees || testResult.summary?.totalEmployees || 0}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Matched"
              value={testResult.matchResults?.matched || testResult.matchStats?.matchedCount || 0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Unmatched"
              value={testResult.matchResults?.unmatched || testResult.matchStats?.unmatchedCount || testResult.unmatched?.length || 0}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="Parse Errors"
              value={testResult.parseErrors?.length || 0}
              valueStyle={{
                color: (testResult.parseErrors?.length || 0) > 0 ? '#ff4d4f' : '#52c41a',
              }}
            />
          </Card>
        </Col>
      </Row>

      {testResult.parseErrors?.length > 0 && (
        <Alert
          message="Parse Errors"
          description={
            <List
              size="small"
              dataSource={testResult.parseErrors}
              renderItem={(err) => (
                <List.Item>
                  <Text type="danger">{err.filename || err.sheet}: {err.error}</Text>
                </List.Item>
              )}
            />
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {(testResult.unmatched?.length > 0 || testResult.ambiguous?.length > 0) && (
        <Alert
          message="Matching Issues"
          description={
            <div>
              {testResult.unmatched?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Unmatched ({testResult.unmatched.length}):</Text>
                  <div>
                    {testResult.unmatched.slice(0, 5).map((u, i) => (
                      <Tag key={i} color="red">{u.name || u.jmbg}</Tag>
                    ))}
                    {testResult.unmatched.length > 5 && <Text type="secondary">... and {testResult.unmatched.length - 5} more</Text>}
                  </div>
                </div>
              )}
              {testResult.ambiguous?.length > 0 && (
                <div>
                  <Text strong>Ambiguous ({testResult.ambiguous.length}):</Text>
                  <div>
                    {testResult.ambiguous.slice(0, 5).map((a, i) => (
                      <Tag key={i} color="orange">{a.name}</Tag>
                    ))}
                    {testResult.ambiguous.length > 5 && <Text type="secondary">... and {testResult.ambiguous.length - 5} more</Text>}
                  </div>
                </div>
              )}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {testResult.validation && (testResult.validation.warnings?.length > 0 || testResult.validation.errors?.length > 0) && (
        <Alert
          message="Validation Warnings"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {testResult.validation.warnings?.map((w, i) => (
                <li key={i}>{w.message || w}</li>
              ))}
              {testResult.validation.errors?.map((e, i) => (
                <li key={i} style={{ color: '#ff4d4f' }}>{e.message || e}</li>
              ))}
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title="Parsed Employees"
        size="small"
        extra={
          <Space>
            <Button size="small" onClick={handleReset}>
              Test Another
            </Button>
            <Button
              type="primary"
              size="small"
              icon={<FileAddOutlined />}
              onClick={handleOpenSubmissionModal}
              disabled={(testResult.employees?.length || 0) === 0}
            >
              Create Submission
            </Button>
          </Space>
        }
      >
        <Table
          columns={employeeColumns}
          dataSource={testResult.employees || []}
          rowKey={(record, index) => record.jmbg || record.employeeName || record.officialName || index}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No employees parsed"
              />
            ),
          }}
        />
      </Card>

      {testResult.matchResults?.report && (
        <Card title="Match Report" size="small" style={{ marginTop: 16 }}>
          <pre style={{ fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
            {testResult.matchResults.report}
          </pre>
        </Card>
      )}

      {/* Create Submission Modal */}
      <Modal
        title={
          <Space>
            <FileAddOutlined />
            <span>Create Payroll Submission</span>
          </Space>
        }
        open={showSubmissionModal}
        onCancel={() => setShowSubmissionModal(false)}
        footer={null}
        width={500}
      >
        <Alert
          message={`Creating submission with ${testResult?.employees?.length || 0} employees`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form
          form={submissionForm}
          layout="vertical"
          onFinish={handleCreateSubmission}
        >
          <Form.Item
            name="companyId"
            label="Company"
            rules={[{ required: true, message: 'Please select a company' }]}
          >
            <Select
              placeholder="Select automation company"
              showSearch
              optionFilterProp="children"
            >
              {automationCompanies.map((company) => (
                <Option key={company._id} value={company._id}>
                  {company.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="month"
                label="Month"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select placeholder="Select month">
                  {[
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December',
                  ].map((m, i) => (
                    <Option key={i + 1} value={i + 1}>{m}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Year"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  min={2020}
                  max={2100}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="projectCode"
            label="Project Code (Optional)"
          >
            <Input placeholder="Enter project code (e.g., UZICE, BG)" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowSubmissionModal(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SendOutlined />}
                loading={createSubmissionMutation.isPending}
              >
                Create Submission
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0 }}>
          <PlayCircleOutlined /> Test Playground (Parse Mode)
        </Title>
        <Text type="secondary">
          Test parser configurations with EML files and optionally match against AB Soft employee list.
        </Text>
      </div>

      {availableConfigs.length === 0 ? (
        <Alert
          message="No Configurations Available"
          description="Create a configuration in the Format Analyzer tab first."
          type="info"
          showIcon
        />
      ) : (
        <>
          {renderTestForm()}
          {renderActions()}

          {isLoading && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Processing EML file...</Text>
              </div>
            </div>
          )}

          {testResult && !isLoading && (
            <>
              <Divider />
              {renderTestResults()}
            </>
          )}
        </>
      )}

      {/* Config Suggestion Modal */}
      <Modal
        title={
          <Space>
            <BulbOutlined />
            <span>Auto-suggest Configuration</span>
          </Space>
        }
        open={showSuggestionPanel}
        onCancel={() => setShowSuggestionPanel(false)}
        footer={null}
        width={700}
        destroyOnClose
      >
        <ConfigSuggestionPanel
          onConfigSelected={handleSuggestedConfigSelect}
          onCreateNew={() => {
            setShowSuggestionPanel(false);
            message.info('Navigate to Format Analyzer to create a new configuration');
          }}
        />
      </Modal>
    </div>
  );
}
